import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, ButtonsWrapper, Link, Info } from "components/elements";
import { WarningModal } from "components/modals";
import { RestrictedArea, ROLES } from "containers/Permissions";
import { TiVoAppProvider, TiVoAppVariantVersion } from "hooks/tivoApps/types";
import { trans } from "src/translations";
import { pathToUrl } from "utils/url";

import { PATHS, VARIANT_DRAFT_ID } from "../../constants";
import { getNextReleaseStage } from "../../utils";
import { TEXTS } from "../constants";
import {
  useVariantPromotion,
  VariantPromotionWarning,
} from "../hooks/useVariantPromotion";
import styles from "./VariantButtons.scss";

export type VariantButtonsProps = {
  variantVersion: TiVoAppVariantVersion;
  provider: TiVoAppProvider;
};

export const VARIANT_WARNING_DATA_TEST_IDS = {
  images: "missing-assets-modal",
  targets: "targets-warning-modal",
  publishingConfirmation: "publishing-confirmation-modal",
} as const;

export function VariantButtons({
  variantVersion,
  provider,
}: VariantButtonsProps) {
  const { id, variantId } = useParams();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<VariantPromotionWarning[]>([]);

  const nextReleaseStage = useMemo(
    () =>
      variantVersion.releaseStages &&
      getNextReleaseStage(variantVersion.releaseStages),
    [variantVersion.releaseStages]
  );

  const handleWarningProceed = () => {
    if (warnings.length === 1) {
      proceedWithPromotion();
    }
    setWarnings((w) => w.slice(1));
  };

  const { promote, promoteAndPublish, proceedWithPromotion, isLoading, error } =
    useVariantPromotion({
      id,
      variantId,
      variantVersion,
      provider,
      onValidationError: setValidationError,
      onWarnings: setWarnings,
    });

  const getInfoMessage = () => {
    if (validationError) {
      return (
        <span>
          {validationError + ". "}
          <RestrictedArea allowed={[ROLES.tivo.provider]} showFallback={false}>
            <Link
              to={pathToUrl(PATHS.editProvider, {
                id,
                tabId: "media",
              })}
            >
              Click here
            </Link>
            {` to upload assets for ${provider.label}.`}
          </RestrictedArea>
        </span>
      );
    }

    if (error) {
      return typeof error === "string"
        ? error
        : error.message || trans.DEFAULT_ERROR_MESSAGE();
    }

    return null;
  };

  const hasError = error || validationError;
  const infoMessage = getInfoMessage();

  return (
    <>
      {infoMessage && (
        <Info type={hasError ? "error" : "warning"}>{infoMessage}</Info>
      )}
      <ButtonsWrapper>
        <RestrictedArea allowed={[ROLES.tivo.provider]} showFallback={false}>
          <Button
            disabled={isLoading}
            to={getVariantEditUrl(id!, variantId, variantVersion.id)}
            dataTestId="edit-variant-button"
          >
            {TEXTS.VARIANT.VARIANT_EDIT_BUTTON}
          </Button>
        </RestrictedArea>
        {nextReleaseStage && (
          <RestrictedArea allowed={[ROLES.tivo.moderator]} showFallback={false}>
            <Button
              disabled={isLoading}
              processing={isLoading}
              type="green"
              dataTestId="promote-variant-button"
              onClick={() => promote(nextReleaseStage)}
            >
              {TEXTS.VARIANT.PROMOTE_BUTTON_LABEL.replace(
                "#{releaseStage}",
                nextReleaseStage
              )}
            </Button>
            <Button
              type="navy"
              disabled={isLoading}
              processing={isLoading}
              dataTestId="promote-and-publish-variant-button"
              onClick={() => promoteAndPublish(nextReleaseStage)}
            >
              {TEXTS.VARIANT.PROMOTE_AND_PUBLISH_BUTTON_LABEL.replace(
                "#{releaseStage}",
                nextReleaseStage
              )}
            </Button>
          </RestrictedArea>
        )}
      </ButtonsWrapper>
      <WarningModal
        actions={
          <ButtonsWrapper position="modal">
            <Button
              type="normal"
              dataTestId={`${VARIANT_WARNING_DATA_TEST_IDS.images}-proceed-button`}
              onClick={handleWarningProceed}
            >
              {TEXTS.VARIANT.PROCEED_BUTTON}
            </Button>
            <RestrictedArea
              allowed={[ROLES.tivo.provider]}
              showFallback={false}
            >
              <Button
                type="green"
                to={pathToUrl(PATHS.editProvider, {
                  id,
                  tabId: "media",
                })}
                dataTestId={`${VARIANT_WARNING_DATA_TEST_IDS.images}-upload-button`}
              >
                {TEXTS.VARIANT.UPLOAD_MISSING_IMAGES}
              </Button>
            </RestrictedArea>
          </ButtonsWrapper>
        }
        isOpen={warnings[0]?.type === "images"}
        size="medium"
        title="Warning"
        dataTestId={VARIANT_WARNING_DATA_TEST_IDS.images}
        onClose={() => setWarnings([])}
      >
        <p>{TEXTS.VARIANT.MISSING_IMAGES_WARNING}</p>
      </WarningModal>
      <WarningModal
        actions={
          <ButtonsWrapper position="modal">
            <Button
              type="normal"
              dataTestId={`${VARIANT_WARNING_DATA_TEST_IDS.targets}-cancel-button`}
              onClick={() => setWarnings((w) => w.slice(1))}
            >
              {TEXTS.VARIANT.PROMOTE_CONFIRM_UNPUBLISHING_NO}
            </Button>
            <Button
              type="green"
              dataTestId={`${VARIANT_WARNING_DATA_TEST_IDS.targets}-proceed-button`}
              onClick={handleWarningProceed}
            >
              {TEXTS.VARIANT.PROMOTE_CONFIRM_UNPUBLISHING_YES}
            </Button>
          </ButtonsWrapper>
        }
        isOpen={warnings[0]?.type === "targets"}
        size="medium"
        title="Warning"
        onClose={() => setWarnings([])}
        dataTestId={VARIANT_WARNING_DATA_TEST_IDS.targets}
      >
        <p>{TEXTS.VARIANT.PROMOTE_CONFIRM_UNPUBLISHING_DESCRIPTION}</p>
      </WarningModal>
      <WarningModal
        actions={
          <ButtonsWrapper position="modal">
            <Button
              type="normal"
              dataTestId={`${VARIANT_WARNING_DATA_TEST_IDS.publishingConfirmation}-cancel-button`}
              onClick={() => setWarnings([])}
            >
              {TEXTS.VARIANT.PUBLISHING_CONFIRMATION_CANCEL}
            </Button>
            <Button
              type="green"
              dataTestId={`${VARIANT_WARNING_DATA_TEST_IDS.publishingConfirmation}-proceed-button`}
              onClick={handleWarningProceed}
            >
              {TEXTS.VARIANT.PUBLISHING_CONFIRMATION_PROCEED}
            </Button>
          </ButtonsWrapper>
        }
        isOpen={warnings[0]?.type === "publishingConfirmation"}
        size="medium"
        title="Warning"
        dataTestId={VARIANT_WARNING_DATA_TEST_IDS.publishingConfirmation}
        onClose={() => setWarnings([])}
      >
        <p>
          <span className={styles.publishingConfirmationMessage}>
            {warnings[0]?.message}
          </span>
          <br />
          {TEXTS.VARIANT.PUBLISHING_CONFIRMATION}
        </p>
      </WarningModal>
    </>
  );
}

function getVariantEditUrl(
  id: string,
  variantId?: string,
  versionId?: string
): string {
  if (variantId === VARIANT_DRAFT_ID) {
    return pathToUrl(PATHS.createVariant, {
      id,
      tabId: "",
    });
  }

  return pathToUrl(PATHS.editVariant, {
    id,
    variantId,
    versionId,
    tabId: "",
  });
}
